<template>
  <v-dialog v-model="dialog" persistent max-width="1200px" scrollable>
    <Create
      :createDialog="categoryCreateDialog"
      @close="categoryCreateDialog = false"
      @submit="getCategory"
    />

    <DepartmentCreate
      :createDialog="departmentCreateDialog"
      @close="departmentCreateDialog = false"
      @submit="getDepartment"
    />

    <BrandCreate
      :createDialog="brandCreateDialog"
      @close="brandCreateDialog = false"
      @submit="getBrand"
    />

    <v-card class="product-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">New Product</div>
          <div class="dialog-subtitle">
            Add product details, pricing, inventory settings, and online order
            options.
          </div>
        </div>

        <v-btn icon small @click.stop.prevent="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text v-if="Product" class="dialog-body pa-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Image, Name & Codes</div>

              <v-row dense>
                <v-col cols="12">
                  <ImageView
                    :name="searchName"
                    :brand="productBrandName"
                    :upc="Product.upc"
                    :sku="Product.sku"
                    field="product"
                    :initUrl="Product.imageUrl"
                    :autoRepair="generateAiProductImage"
                    @submit="imageChange"
                    @auto-image-disabled="setAutoImageDisabled"
                    @uploaded="imageUploaded"
                  />
                </v-col>

                <v-col cols="12" sm="6" v-if="!Product.isParent">
                  <div class="field-label">UPC Code</div>
                  <v-text-field
                    v-model="Product.upc"
                    @focus="$event.target.select()"
                    outlined
                    @click:append="generateRandomUPC"
                    append-icon="mdi-barcode-scan"
                    hide-details
                    dense
                    label="UPC Code"
                  />
                </v-col>

                <v-col cols="12" sm="6" v-if="!Product.isParent">
                  <div class="field-label">SKU Code</div>
                  <v-text-field
                    v-model="Product.sku"
                    @focus="$event.target.select()"
                    outlined
                    @click:append="generateRandomSKU"
                    append-icon="mdi-barcode-scan"
                    hide-details
                    dense
                    label="SKU Code"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="field-label">Product Name</div>
                  <v-text-field
                    v-model="Product.name"
                    :error-messages="nameErrors"
                    :hide-details="nameErrors.length === 0"
                    outlined
                    autofocus
                    @change="changeName"
                    @focus="$event.target.select()"
                    dense
                    label="Name"
                    ref="productName"
                  />
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">Category, Department & Brand</div>

              <v-row dense>
                <v-col cols="12">
                  <div class="field-label">Category</div>
                  <v-select
                    :items="Category"
                    clearable
                    :hide-details="selectCategoryError.length === 0"
                    :error-messages="selectCategoryError"
                    v-model="Product.CategoryId"
                    item-text="name"
                    outlined
                    dense
                    item-value="id"
                    label="Select Category"
                  >
                    <template v-slot:append-item>
                      <v-divider class="mb-2" />
                      <v-list-item @click="categoryCreateDialog = true">
                        <v-list-item-icon>
                          <v-icon color="primary"
                            >mdi-plus-circle-outline</v-icon
                          >
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            >Create New Category</v-list-item-title
                          >
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12">
                  <div class="field-label">Department</div>
                  <v-select
                    :items="Department"
                    clearable
                    hide-details
                    v-model="Product.DepartmentId"
                    item-text="name"
                    outlined
                    dense
                    item-value="id"
                    label="Select Department"
                  >
                    <template v-slot:append-item>
                      <v-divider class="mb-2" />
                      <v-list-item @click="departmentCreateDialog = true">
                        <v-list-item-icon>
                          <v-icon color="primary"
                            >mdi-plus-circle-outline</v-icon
                          >
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            >Create New Department</v-list-item-title
                          >
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12">
                  <div class="field-label">Brand</div>
                  <v-select
                    :items="Brand"
                    clearable
                    hide-details
                    v-model="Product.BrandId"
                    item-text="name"
                    outlined
                    dense
                    item-value="id"
                    label="Select Brand"
                  >
                    <template v-slot:append-item>
                      <v-divider class="mb-2" />
                      <v-list-item @click="brandCreateDialog = true">
                        <v-list-item-icon>
                          <v-icon color="primary"
                            >mdi-plus-circle-outline</v-icon
                          >
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            >Create New Brand</v-list-item-title
                          >
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  v-if="
                    currentBusiness.metadata &&
                    currentBusiness.metadata.parentChile &&
                    !Product.isParent
                  "
                >
                  <div class="field-label">Parent Product</div>
                  <v-select
                    v-if="Products"
                    :items="Products"
                    clearable
                    v-model="Product.ChildProductId"
                    hide-no-data
                    item-text="name"
                    outlined
                    dense
                    item-value="id"
                    label="Select Parent Product"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>

          <v-col cols="12" md="4" v-if="!Product.isParent">
            <div class="form-section">
              <div class="section-title">Purchase & Sale</div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label">Purchase Cost</div>
                  <v-text-field
                    v-model="Product.cost"
                    @focus="$event.target.select()"
                    class="right-input"
                    outlined
                    hide-details
                    dense
                    label="Purchase Cost"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-label">Selling Price</div>
                  <v-text-field
                    v-model="Product.price"
                    class="right-input"
                    label="Selling Price"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  v-if="Product.trackInventory && !Product.trackSerialNumber"
                >
                  <div class="field-label">Opening Balance</div>
                  <v-text-field
                    v-model="Product.initBalance"
                    class="right-input"
                    hide-details
                    :disabled="!checkRightStatus(46)"
                    outlined
                    dense
                    @focus="$event.target.select()"
                    label="Opening Balance"
                  />
                </v-col>

                <v-col cols="12" sm="6" v-if="Product.trackInventory">
                  <div class="field-label">Current Stock</div>
                  <v-text-field
                    readonly
                    v-model="Product.balance"
                    class="right-input"
                    hide-details
                    :disabled="!checkRightStatus(46)"
                    outlined
                    dense
                    @focus="$event.target.select()"
                    label="Current Stock"
                  />
                </v-col>
              </v-row>
            </div>

            <div class="form-section" v-if="Product.trackInventory">
              <div class="section-title">Reorder Point</div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label">Min</div>
                  <v-text-field
                    v-model="Product.min"
                    class="right-input"
                    label="Min"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-label">Max</div>
                  <v-text-field
                    v-model="Product.max"
                    class="right-input"
                    label="Max"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>
              </v-row>
            </div>

            <div
              class="form-section"
              v-if="
                currentBusiness &&
                currentBusiness.metadata &&
                currentBusiness.metadata.boxCountOnInventory
              "
            >
              <div class="section-title">Box Settings</div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label">Unit Count</div>
                  <v-text-field
                    type="number"
                    v-model="Product.unitCountInBox"
                    class="right-input"
                    label="Unit Count"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-label">Unit Price</div>
                  <v-text-field
                    type="number"
                    readonly
                    v-model="Product.unitPrice"
                    class="right-input"
                    label="Unit Price"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">Description</div>

              <v-textarea
                counter="counter"
                hide-details
                outlined
                dense
                auto-grow
                rows="4"
                v-model="Product.note"
                label="Description"
              />
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Product Settings</div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Active</div>
                  <div class="setting-caption">
                    Product can be used for sales and purchases.
                  </div>
                </div>
                <v-switch
                  color="primary"
                  v-model="Product.active"
                  inset
                  hide-details
                />
              </div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Parent Product</div>
                  <div class="setting-caption">
                    Use this as a group for sub-products.
                  </div>
                </div>
                <v-switch
                  color="primary"
                  :disabled="Product.trackSerialNumber"
                  v-model="Product.isParent"
                  inset
                  hide-details
                />
              </div>

              <v-divider class="my-3" />

              <div class="setting-row">
                <div>
                  <div class="setting-title">Inventory</div>
                  <div class="setting-caption">Track product stock count.</div>
                </div>
                <v-switch
                  v-model="Product.trackInventory"
                  color="primary"
                  :disabled="trackInventory"
                  inset
                  hide-details
                />
              </div>

              <div
                class="setting-row"
                v-if="
                  currentBusiness.metadata &&
                  currentBusiness.metadata.serializeProduct
                "
              >
                <div>
                  <div class="setting-title">Serial Track</div>
                  <div class="setting-caption">
                    Track stock using serial numbers.
                  </div>
                </div>
                <v-switch
                  color="primary"
                  v-model="Product.trackSerialNumber"
                  inset
                  hide-details
                />
              </div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Remote Order</div>
                  <div class="setting-caption">Show this product online.</div>
                </div>
                <v-switch
                  v-model="Product.remoteOrder"
                  color="primary"
                  inset
                  hide-details
                />
              </div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Special</div>
                  <div class="setting-caption">
                    Mark as special on remote ordering.
                  </div>
                </div>
                <v-switch
                  v-model="Product.special"
                  color="primary"
                  inset
                  hide-details
                />
              </div>

              <div class="setting-row">
                <div>
                  <div class="setting-title">Weight</div>
                  <div class="setting-caption">Ask for weight during sale.</div>
                </div>
                <v-switch
                  color="primary"
                  v-model="Product.metadata.weightApply"
                  inset
                  hide-details
                />
              </div>

              <div
                class="setting-row"
                v-if="
                  currentBusiness.metadata &&
                  currentBusiness.metadata.dynamicPrice
                "
              >
                <div>
                  <div class="setting-title">Standard Price</div>
                  <div class="setting-caption">
                    Exclude from dynamic pricing.
                  </div>
                </div>
                <v-switch
                  color="primary"
                  v-model="Product.metadata.standardPrice"
                  inset
                  hide-details
                />
              </div>

              <div
                class="setting-row"
                v-if="
                  currentBusiness &&
                  currentBusiness.metadata &&
                  !currentBusiness.metadata.isSingleCustomerPricing
                "
              >
                <div>
                  <div class="setting-title">Price Tier</div>
                  <div class="setting-caption">
                    Set prices for different tiers.
                  </div>
                </div>

                <ProductPriceTier
                  @save="priceTierSave"
                  :price="Product.price"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn color="primary" text @click="close"> Close </v-btn>
        <v-btn
          color="primary"
          depressed
          rounded
          :loading="loading"
          dark
          @click="submit"
        >
          Save Product
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import productService from "../service.js";
import categoryService from "@/modules/Product/Category/service";
import departmentService from "@/modules/Product/Department/service";
import brandService from "@/modules/Product/Brand/service";
import GlobalFunction from "@/mixins/GlobalFunction";
import ImageView from "@/components/Image/ImageView";
import { mapGetters } from "vuex";
import ProductPriceTier from "@/modules/Product/PriceTier/components/InProductPriceTier";
const { validationMixin } = require("vuelidate");
const { required, minLength, minValue } = require("vuelidate/lib/validators");
const Create = () => import("@/modules/Product/Category/components/Create");
const DepartmentCreate = () =>
  import("@/modules/Product/Department/components/Create");
const BrandCreate = () => import("@/modules/Product/Brand/components/Create");

export default {
  props: {
    createDialog: {
      default: false,
    },
  },
  name: "create-product",
  data() {
    return {
      selectedProduct: null,
      productSearchLoading: false,
      Products: [],
      categoryCreateDialog: false,
      departmentCreateDialog: false,
      brandCreateDialog: false,
      Category: [],
      Department: [],
      Brand: [],
      searchName: null,
      imageResult: null,
      selectedCategory: null,
      loading: false,
      dialog: false,
      trackInventory: true,
      Product: {
        initBalance: 0,
        upc: null,
        sku: null,
        isParent: false,
        name: null,
        note: null,
        imageUrl: null,
        thumbnailUrl: null,
        price: 0,
        cost: 0,
        boxPrice: null,
        unitCountInBox: null,
        trackSerialNumber: false,
        balance: 0,
        remoteOrder: true,
        special: false,
        trackInventory: true,
        min: null,
        max: null,
        active: true,
        metadata: {
          weightApply: false,
          standardPrice: false,
        },
      },
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Product: {
      name: {
        required,
        minLength: minLength(4),
      },
      CategoryId: {
        required,
        minValue: minValue(1),
      },
    },
  },
  components: {
    // MoreImage,
    ProductPriceTier,
    ImageView,
    Create,
    BrandCreate,
    DepartmentCreate,
  },
  computed: {
    ...mapGetters("global", [
      "comboBox",
      "comboBoxByID",
      "checkRightStatus",
      "currentUser",
      "currentBusiness",
    ]),
    productBrandName() {
      const selected = this.Brand.find((brand) => brand.id === this.Product.BrandId);
      return selected ? selected.name : null;
    },
    generateAiProductImage() {
      return !(
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.generateAiProductImage === false
      );
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Product.name.$dirty) return errors;
      if (!this.$v.Product.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.Product.name.minLength)
        errors.push("Field must be at least 4 characters long");
      return errors;
    },
    selectCategoryError() {
      const errors = [];
      if (!this.$v.Product.CategoryId.$dirty) return errors;
      if (!this.$v.Product.CategoryId.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.Product.CategoryId.minValue)
        errors.push("Pleaes select category");

      return errors;
    },
  },
  watch: {
    // 'Product.trackSerialNumber': function (val) {
    //   if (!val) {
    //     this.Product.trackInventory = true
    //   }
    // },
    "Product.isParent": function (val) {
      if (!val) {
        this.selectedProduct = null;
      }
    },
    "Product.unitCountInBox": function () {
      this.updateboxPrice();
    },
    "Product.price": function () {
      this.updateboxPrice();
    },
    Product: {
      handler(val) {
        if (val.trackSerialNumber) {
          this.Product.trackInventory = true;
          this.trackInventory = true;
        } else {
          this.trackInventory = false;
        }
      },
      deep: true,
    },
    // Product: {
    //   handler(val) {
    //     // console.log('val', val)
    //     // if (val.trackInventory) {
    //     //   if (val.price && val.unitCountInBox) {
    //     //     val.boxPrice = val.price * val.unitCountInBox
    //     //   }
    //     //   if (val.boxPrice && val.unitCountInBox) {
    //     //     console.log('in heer', val.boxPrice, val.unitCountInBox, val.price)
    //     //     val.price = val.boxPrice / val.unitCountInBox
    //     //   }
    //     // }

    //     if (val.trackSerialNumber) {
    //       this.Product.trackInventory = true;
    //       this.trackInventory = true;
    //     } else {
    //       this.trackInventory = false;
    //     }
    //   },
    //   deep: true,
    // },
    createDialog(val) {
      this.dialog = val;
      if (val) {
        this.getCategory();
        this.getDepartment();
        this.getBrand();
        this.$nextTick(() => {
          this.$refs.productName.focus();
          // console.log('this.custo', this.currentBusiness.metadata.ForcedInventoryTracking)
          if (this.currentBusiness?.metadata?.ForcedInventoryTracking) {
            this.Product.trackInventory = true;
            this.trackInventory = true;
          }
        });
      }
    },
  },
  methods: {
    imageUploaded(file) {
      if (!file) return;

      if (file.imageUrl) {
        this.Product.imageUrl = file.imageUrl;
      } else if (file.url) {
        this.Product.imageUrl = file.url;
      } else if (typeof file === "string") {
        this.Product.imageUrl = file;
      }

      if (file.thumbnailUrl) {
        this.Product.thumbnailUrl = file.thumbnailUrl;
      }

      if (file.imageSource) {
        this.Product.metadata = {
          ...(this.Product.metadata || {}),
          autoImageDisabled: file.imageSource !== "auto-search",
          imageSource: file.imageSource,
        };
      }
    },
    priceTierSave(val) {
      // console.log("price tier save", val);
      this.Product.PriceTiers = val;
    },
    generateRandomUPC() {
      let upc = "C"; // Start with 'C'
      for (let i = 1; i < 12; i++) {
        // Start the loop from 1 because 'C' is already added
        upc += Math.floor(Math.random() * 10); // Generate a random digit (0-9)
      }
      this.Product.upc = upc;
    },
    generateRandomSKU() {
      let sku = "K"; // Start with 'C'
      for (let i = 1; i < 12; i++) {
        // Start the loop from 1 because 'C' is already added
        sku += Math.floor(Math.random() * 10); // Generate a random digit (0-9)
      }
      console.log("generated SKU", sku);
      this.Product.sku = sku;
    },
    getProduct() {
      this.loading = true;

      return productService.getAll({ isParent: true }).then((result) => {
        // console.log("get prodcut", result);
        this.Products = result.data;
        this.loading = false;
      });
    },
    updateboxPrice() {
      if (
        this.Product.trackInventory &&
        this.Product.unitCountInBox > 0 &&
        this.Product.price > 0
      ) {
        this.Product.boxPrice = (
          this.Product.price * this.Product.unitCountInBox
        ).toFixed(2);
        this.Product.boxPrice = parseFloat(this.Product.boxPrice);
      }
    },
    imageChange(url) {
      this.Product.imageUrl = url;

      if (!url) {
        this.Product.thumbnailUrl = null;
      }
    },
    setAutoImageDisabled(disabled) {
      this.Product.metadata = {
        ...(this.Product.metadata || {}),
        autoImageDisabled: disabled,
      };
    },
    changeName() {
      this.Product.name = this.properCase(this.Product.name);
      this.searchName = this.Product.name;
      // this.getimage(this.Product.name).then(response => {
      //   console.log('response', response)
      //   if (response.data) {
      //     this.imageResult = response.data;
      //     this.Product.imageUrl = response.data.imgUrl1;
      //   }
      // });
    },
    getimage(name) {
      return productService
        .getGooglePic({ name: name })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.log("error", error);
        });
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    getCategory() {
      this.loading = true;
      return categoryService.getAll().then((response) => {
        this.loading = false;
        this.Category = response.data;
        return response;
      });
    },
    getDepartment() {
      this.loading = true;
      return departmentService.getAll().then((response) => {
        this.loading = false;
        this.Department = response.data;
        return response;
      });
    },
    getBrand() {
      this.loading = true;
      return brandService.getAll().then((response) => {
        this.loading = false;
        this.Brand = response.data;
        return response;
      });
    },
    empty() {
      this.Product = {
        initBalance: 0,
        upc: null,
        sku: null,
        isParent: false,
        name: null,
        note: null,
        imageUrl: null,
        thumbnailUrl: null,
        price: 0,
        cost: 0,
        boxPrice: null,
        unitCountInBox: null,
        trackSerialNumber: false,
        balance: 0,
        remoteOrder: true,
        special: false,
        trackInventory: true,
        min: null,
        max: null,
        active: true,
        metadata: {
          weightApply: false,
          standardPrice: false,
        },
      };
      // this.Product = {
      //   isParent: false,
      //   name: null,
      //   note: null,
      //   price: null,
      //   cost: 0,
      //   boxPrice: null,
      //   unitCountInBox: null,
      //   min: null,
      //   max: null,
      //   imageUrl: null,
      //   CategoryId: null,
      //   trackSerialNumber: false,
      //   balance: 0,
      //   trackInventory: false,
      //   active: true,
      //   metadata: {
      //     weightApply: false,
      //   },
      this.$v.$reset();
    },
    submit() {
      if (!this.Product.min && this.Product.max) {
        // console.log('in here')
        this.Product.min = parseInt(this.Product.max / 2);
      }

      if (!this.Product.max && this.Product.min) {
        // console.log('in here')
        this.Product.max = parseInt(this.Product.min * 2);
      }

      if (this.Product.max < this.Product.min) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Max balance can't be less than min balance",
          });
      }

      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        // if (this.imageUrl) {
        //   this.Product.image = {
        //     imageUrl: this.imageUrl,
        //     TypeId: 9,
        //   };
        // }
        this.loading = true;
        if (this.Product.isParent) {
          this.Product.trackInventory = false;
          this.Product.trackSerialNumber = false;
          this.Product.ChildProductId = null;
        }

        return productService
          .create(this.Product)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Product is created",
                });
              this.empty();
              this.loading = false;
              this.$emit("submit");
            }
          })
          .catch((err) => {
            // console.log("error", err);
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped>
.product-dialog-card {
  border-radius: 20px;
  overflow: hidden;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 auto;
  background: #ffffff;
}

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.dialog-actions {
  padding: 12px 16px;
  flex: 0 0 auto;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 0;
}

.setting-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.setting-caption {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  max-width: 260px;
}

.dialog-actions {
  padding: 12px 16px;
}

.right-input input {
  text-align: right;
}

@media (max-width: 600px) {
  .dialog-header {
    padding: 14px 16px;
  }

  .dialog-title {
    font-size: 18px;
  }

  .form-section {
    padding: 12px;
  }

  .setting-row {
    align-items: flex-start;
  }
}
</style>
